import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './kiosk.css';
import { FaPlus, FaMinus, FaTrash, FaSearch, FaLeaf } from 'react-icons/fa';
import { UserContext } from '../../Context/UserContext';
import KioskVariantModal from './KioskVariantModal';
import { v4 as uuidv4 } from 'uuid';

// ── Map any size string → 'small' | 'medium' | 'large' ────────────────────────
const normalizeSize = (sz) => {
  const map = {
    s: 'small',  small: 'small',  Small: 'small',
    m: 'medium', medium: 'medium', Medium: 'medium',
    l: 'large',  large: 'large',  Large: 'large',
  };
  return map[sz] || (sz || '').toLowerCase();
};

// ── Look up the size-adjusted price from an entity's size config ──────────────
// Defaults to 'Medium' when no explicit size stored (matches modal default)
const sizeAdjustedPrice = (ev, sizeLabel, fallbackPrice) => {
  const key = normalizeSize(sizeLabel || 'Medium') + '_price'; // 'medium_price'
  const p   = ev.size[key];
  return (typeof p === 'number' && p > 0) ? p : (fallbackPrice || 0);
};

const KioskPage = () => {
  const [items,               setItems]               = useState([]);
  const [categories,          setCategories]          = useState(['All']);
  const [activeCategory,      setActiveCategory]      = useState('All');
  const [cart,                setCart]                = useState([]);
  const [loading,             setLoading]             = useState(true);
  const [selectedItem,        setSelectedItem]        = useState(null);
  const [selectedCartItem,    setSelectedCartItem]    = useState(null);
  const [nutritionItem,       setNutritionItem]       = useState(null);
  const [searchQuery,         setSearchQuery]         = useState('');
  const [categorySearchQuery, setCategorySearchQuery] = useState('');

  const simulatorModes = [
    { name: 'Native', label: 'Full Screen' },
    { name: '15.6',   label: '15.6" Small counter' },
    { name: '18.5',   label: '18.5" Compact' },
    { name: '21.5',   label: '21.5" Small kiosk' },
    { name: '23.8',   label: '23.8" Very common' },
    { name: '24',     label: '24" Recommended' },
    { name: '27',     label: '27" Large kiosk' },
    { name: '32',     label: '32" Large kiosk' },
  ];
  const [activeSimulator, setActiveSimulator] = useState('Native');
  const [orientation,     setOrientation]     = useState('landscape');

  const navigate = useNavigate();
  const { baseUrl, getHeaders } = React.useContext(UserContext);

  // ─── Fetch Items ─────────────────────────────────────────────────────────────
  useEffect(() => {
    document.body.classList.add('kiosk-mode-active');
    const fetchItems = async () => {
      try {
        const headers = getHeaders ? getHeaders() : { Authorization: `Bearer ${localStorage.getItem('token')}` };
        const apiPath = baseUrl ? `${baseUrl}/api/items` : '/api/items';
        const data    = (await axios.get(apiPath, { headers })).data;

        if (Array.isArray(data)) {
          const activeComp   = localStorage.getItem('active_company')  || '';
          const activeBranch = localStorage.getItem('active_branch')   || '';
          const isBranch     = activeBranch && activeBranch !== 'All Branches' && activeBranch !== 'All';
          const isCompany    = activeComp   && activeComp   !== 'All'           && activeComp   !== 'All Companies';

          const resolvePrice = (raw, bp, cp) => {
            if (isBranch  && bp?.[activeBranch] != null && Number(bp[activeBranch]) > 0) return Number(bp[activeBranch]);
            if (isCompany && cp?.[activeComp]   != null && Number(cp[activeComp])   > 0) return Number(cp[activeComp]);
            return Number(raw) || 0;
          };

          const seen = new Map();
          const addIfNew = (item) => {
            const key = `${(item.item_name || '').trim().toLowerCase()}-${item.type}`;
            if (!seen.has(key)) { seen.set(key, item); return; }
            const flat  = (o) => [o.branch_names, o.branches, o.branch_name ? [o.branch_name] : [], o.branch ? [o.branch] : []]
              .flat().flatMap(b => typeof b === 'string' ? b.split(',').map(s => s.trim().toLowerCase()) : b || []).filter(Boolean);
            const exUni = !flat(seen.get(key)).length || flat(seen.get(key)).some(b => ['global','all','all branches','any'].includes(b));
            const iBr   = flat(item);
            if (isBranch && exUni && iBr.length && !iBr.some(b => ['global','all','all branches','any'].includes(b))) seen.set(key, item);
          };

          data.forEach(item => {
            const group = item.item_group || 'Uncategorized';
            let   type  = 'item';
            if (group.toLowerCase().includes('addon')) type = 'addon';
            if (group.toLowerCase().includes('combo') || item.type === 'combo_offer' || item.type === 'item_combo') type = 'item_combo';
            if (type !== 'item') return;
            addIfNew({
              ...item,
              _id:                   item._id || item.id || Math.random().toString(),
              item_name:             item.item_name || item.name || 'Unnamed Item',
              item_group:            group,
              image_url:             item.image ? `${baseUrl}${item.image}` : (item.image_url || null),
              standard_selling_rate: resolvePrice(item.price_list_rate || item.standard_selling_rate, item.branch_prices, item.company_prices),
              type,
            });
          });

          const finalItems = Array.from(seen.values()).sort((a, b) => (a.item_name || '').localeCompare(b.item_name || ''));
          setItems(finalItems);
          setCategories(['All', ...[...new Set(finalItems.map(i => i.item_group))].filter(c => c !== 'Uncategorized').sort()]);
        }
      } catch (e) { console.error('Kiosk fetch error:', e); }
      finally      { setLoading(false); }
    };
    fetchItems();
    return () => document.body.classList.remove('kiosk-mode-active');
  }, [baseUrl]);

  // ─── Entity variant normaliser ────────────────────────────────────────────────
  const getEntityVariants = (e) => ({
    size:  {
      enabled:      e.variants?.size?.enabled      ?? e.size?.enabled      ?? false,
      small_price:  e.variants?.size?.small_price   ?? e.size?.small_price   ?? 0,
      medium_price: e.variants?.size?.medium_price  ?? e.size?.medium_price  ?? 0,
      large_price:  e.variants?.size?.large_price   ?? e.size?.large_price   ?? 0,
    },
    cold:  { enabled: e.variants?.cold?.enabled  ?? e.cold?.enabled  ?? false, ice_price:   e.variants?.cold?.ice_price   ?? e.cold?.ice_price   ?? 0 },
    spicy: { enabled: e.variants?.spicy?.enabled ?? e.spicy?.enabled ?? false, spicy_price: e.variants?.spicy?.spicy_price ?? e.spicy?.spicy_price ?? 30 },
  });

  // ─── Compute full entity price (same logic as modal) ─────────────────────────
  // isAddon=true → use addon_price, false → combo_price
  // eVars = addonVariants[name]: { size: 'Medium'|'M'|..., cold: bool, spicy: bool }
  const computeEntityFullPrice = (entity, isAddon, eVars, eCVars, eTops) => {
    const ev     = getEntityVariants(entity);
    const rawBase = isAddon ? (entity.addon_price || entity.price || 0) : (entity.combo_price || entity.price || 0);

    // Base price: if size enabled → use size price (default: Medium if no selection stored)
    let price = ev.size.enabled
      ? sizeAdjustedPrice(ev, eVars?.size, rawBase)
      : rawBase;

    // Ice
    if (ev.cold.enabled  && eVars?.cold)  price += ev.cold.ice_price    || 0;
    // Spicy
    if (ev.spicy.enabled && eVars?.spicy) price += ev.spicy.spicy_price || 30;
    // Custom variants
    const cVars = eCVars || {};
    entity.custom_variants?.forEach(v => {
      if (v.enabled) v.subheadings?.forEach(s => { if (cVars[s.name]) price += s.price || 0; });
    });
    // Toppings
    const tops = eTops || {};
    entity.toppings?.forEach(t => { if (tops[t._id || t.topping_name]) price += t.price || 0; });

    return price;
  };

  // ─── Get sub-option cost from entity (everything EXCEPT the size-adjusted base) ─
  const computeEntitySubCost = (entity, isAddon, eVars, eCVars, eTops) => {
    const ev = getEntityVariants(entity);
    let sub  = 0;
    if (ev.cold.enabled  && eVars?.cold)  sub += ev.cold.ice_price    || 0;
    if (ev.spicy.enabled && eVars?.spicy) sub += ev.spicy.spicy_price || 30;
    const cVars = eCVars || {};
    entity.custom_variants?.forEach(v => {
      if (v.enabled) v.subheadings?.forEach(s => { if (cVars[s.name]) sub += s.price || 0; });
    });
    const tops = eTops || {};
    entity.toppings?.forEach(t => { if (tops[t._id || t.topping_name]) sub += t.price || 0; });
    return sub;
  };

  // ─── Build sub-rows from a cart item ─────────────────────────────────────────
  const getSubRows = (item) => {
    const rows   = [];
    const subQty = item.subItemQuantities || {};
    // Always use getEntityVariants so price fallbacks work correctly:
    // modal overwrites item.variants with simplified obj (loses spicy_price),
    // but getEntityVariants also checks item.spicy?.spicy_price from original API spread.
    const ev = getEntityVariants(item);

    if (!item.parentId) {
      if (item.isSpicy)
        rows.push({ key: 'spicy', label: 'Spicy', unitPrice: ev.spicy.spicy_price, qty: subQty['spicy'] ?? 1 });
      if (item.icePreference === 'with_ice')
        rows.push({ key: 'ice', label: 'Ice', unitPrice: ev.cold.ice_price, qty: subQty['ice'] ?? 1 });
      Object.entries(item.customVariantsDetails || {}).forEach(([name, d]) => {
        const label = d.heading ? `${d.heading}: ${d.name}` : d.name;
        rows.push({ key: `cv_${name}`, label, unitPrice: d.price || 0, qty: subQty[`cv_${name}`] ?? 1 });
      });
      Object.entries(item.itemToppings || {}).forEach(([id, t]) => {
        rows.push({ key: `tp_${id}`, label: `${t.name} (Topping)`, unitPrice: t.price || 0, qty: subQty[`tp_${id}`] ?? 1 });
      });
    } else {
      // Child items store correct variant prices in item.variants (set during handleItemUpdate)
      if (item.isSpicy)
        rows.push({ key: 'spicy', label: 'Spicy', unitPrice: ev.spicy.spicy_price, qty: subQty['spicy'] ?? 1 });
      if (item.icePreference === 'with_ice')
        rows.push({ key: 'ice', label: 'Ice', unitPrice: ev.cold.ice_price, qty: subQty['ice'] ?? 1 });
      (item.customVariantDetails || []).forEach((d, i) => {
        const label = d.heading ? `${d.heading}: ${d.name}` : d.name;
        rows.push({ key: `cv_${i}`, label, unitPrice: d.price || 0, qty: subQty[`cv_${i}`] ?? 1 });
      });
      (item.toppingDetails || []).forEach((t, i) => {
        rows.push({ key: `tp_${i}`, label: `${t.name} (Topping)`, unitPrice: t.price || 0, qty: subQty[`tp_${i}`] ?? 1 });
      });
    }
    return rows;
  };

  // ─── Compute sub-total from stored subItemQuantities ─────────────────────────
  const computeSubTotal = (item, overrideSubQty) => {
    const subQty = overrideSubQty || item.subItemQuantities || {};
    // Use getEntityVariants so price lookup handles all fallback paths
    const ev     = getEntityVariants(item);
    let total = 0;
    if (!item.parentId) {
      if (item.isSpicy)                        total += ev.spicy.spicy_price * (subQty['spicy'] ?? 1);
      if (item.icePreference === 'with_ice')    total += ev.cold.ice_price   * (subQty['ice']   ?? 1);
      Object.entries(item.customVariantsDetails || {}).forEach(([n, d]) => { total += (d.price || 0) * (subQty[`cv_${n}`] ?? 1); });
      Object.entries(item.itemToppings       || {}).forEach(([id, t]) => { total += (t.price || 0) * (subQty[`tp_${id}`] ?? 1); });
    } else {
      if (item.isSpicy)                        total += ev.spicy.spicy_price * (subQty['spicy'] ?? 1);
      if (item.icePreference === 'with_ice')    total += ev.cold.ice_price   * (subQty['ice']   ?? 1);
      (item.customVariantDetails || []).forEach((d, i) => { total += (d.price || 0) * (subQty[`cv_${i}`] ?? 1); });
      (item.toppingDetails       || []).forEach((t, i) => { total += (t.price || 0) * (subQty[`tp_${i}`] ?? 1); });
    }
    return total;
  };

  // ─── Init subItemQuantities ───────────────────────────────────────────────────
  const buildInitialSubQty = (item) => {
    const q = {};
    if (item.isSpicy)                        q['spicy'] = 1;
    if (item.icePreference === 'with_ice')    q['ice']   = 1;
    Object.keys(item.customVariantsDetails || {}).forEach(n  => { q[`cv_${n}`] = 1; });
    Object.keys(item.itemToppings       || {}).forEach(id => { q[`tp_${id}`] = 1; });
    return q;
  };

  const buildChildSubQty = (child) => {
    const q = {};
    if (child.isSpicy)                       q['spicy'] = 1;
    if (child.icePreference === 'with_ice')   q['ice']   = 1;
    (child.customVariantDetails || []).forEach((_, i) => { q[`cv_${i}`] = 1; });
    (child.toppingDetails       || []).forEach((_, i) => { q[`tp_${i}`] = 1; });
    return q;
  };

  // ─── Add to Cart ──────────────────────────────────────────────────────────────
  const addToCart = (item) => {
    const hasOptions =
      item.size?.enabled || item.cold?.enabled || item.spicy?.enabled ||
      (item.custom_variants?.length > 0) || (item.toppings?.length > 0) ||
      (item.addons?.length > 0) || (item.combos?.length > 0);
    if (hasOptions) setSelectedItem(item);
    else handleItemUpdate({ ...item, qty: 1, quantity: 1, totalPrice: item.standard_selling_rate || 0 }, true);
  };

  // ─── Handle Modal Save ────────────────────────────────────────────────────────
  const handleItemUpdate = (updatedItem, forceAdd = false) => {
    const mainItemId = selectedCartItem?.id || uuidv4();

    // ── Main item base price (size-adjusted only, matches modal logic) ────────
    const mainEv  = getEntityVariants(updatedItem);
    const rawMain = updatedItem.price_list_rate || updatedItem.standard_selling_rate || 0;
    const baseItemPrice = mainEv.size.enabled
      ? sizeAdjustedPrice(mainEv, updatedItem.selectedSize, rawMain)
      : rawMain;

    const qty        = updatedItem.qty || updatedItem.quantity || 1;
    const subItemQty = buildInitialSubQty(updatedItem);
    const subTotal   = computeSubTotal({ ...updatedItem, subItemQuantities: subItemQty });
    const totalPrice = baseItemPrice * qty + subTotal;

    const mainCartEntry = {
      ...updatedItem,
      id:            mainItemId,
      item_name:     updatedItem.item_name || updatedItem.name,
      qty,
      quantity:      qty,
      baseItemPrice,
      totalPrice,
      subItemQuantities:     subItemQty,
      selectedSize:          updatedItem.selectedSize  || null,
      icePreference:         updatedItem.icePreference || 'without_ice',
      isSpicy:               updatedItem.isSpicy       || false,
      // ── Preserve all addon/combo selections so modal restores them on re-edit ──
      addonQuantities:       updatedItem.addonQuantities       || {},
      addonVariants:         updatedItem.addonVariants         || {},
      addonCustomVariants:   updatedItem.addonCustomVariants   || {},
      addonToppings:         updatedItem.addonToppings         || {},
      comboQuantities:       updatedItem.comboQuantities       || {},
      comboVariants:         updatedItem.comboVariants         || {},
      comboCustomVariants:   updatedItem.comboCustomVariants   || {},
      comboToppings:         updatedItem.comboToppings         || {},
    };

    // ── Build addon child items ────────────────────────────────────────────────
    const childItems = [];

    Object.entries(updatedItem.addonQuantities || {}).forEach(([addonName, addonQty]) => {
      if (addonQty <= 0) return;
      const addonData = updatedItem.addons?.find(a => (a.name1 || a.name) === addonName);
      if (!addonData) return;

      const addonVars  = updatedItem.addonVariants?.[addonName]       || {};
      const addonCVars = updatedItem.addonCustomVariants?.[addonName] || {};
      const addonTops  = updatedItem.addonToppings?.[addonName]       || {};
      const ev         = getEntityVariants(addonData);

      // ── Key fix: base price uses size default 'Medium' if none stored ────────
      const rawAddon  = addonData.addon_price || addonData.price || 0;
      // addonVars.size could be 'Medium', 'M', etc. — or undefined (modal default = 'Medium')
      const addonBase = ev.size.enabled
        ? sizeAdjustedPrice(ev, addonVars.size, rawAddon)   // handles 'Medium' default
        : rawAddon;

      // Build detailed custom variant info for billing display
      const customVariantDetails = [];
      addonData.custom_variants?.forEach(v => {
        if (v.enabled) v.subheadings?.forEach(sub => {
          if (addonCVars[sub.name]) customVariantDetails.push({
            heading: v.heading || v.name || '',
            name: sub.name,
            price: sub.price || 0,
          });
        });
      });

      // Build detailed topping info for billing display
      const toppingDetails = Object.entries(addonTops)
        .filter(([, sel]) => sel)
        .map(([id]) => {
          const t = addonData.toppings?.find(t => t._id === id || t.topping_name === id);
          return t ? { name: t.topping_name || t.name || id, price: t.price || 0 } : null;
        }).filter(Boolean);

      const isAddonSpicy = addonVars.spicy || false;
      const isAddonIce   = addonVars.cold  ? 'with_ice' : 'without_ice';

      const childItem = {
        _id:                  Math.random().toString(),
        id:                   uuidv4(),
        parentId:             mainItemId,
        item_name:            addonName,
        item_group:           'Addons',
        type:                 'addon',
        qty:                  addonQty,
        quantity:             addonQty,
        baseItemPrice:        addonBase,
        standard_selling_rate: addonBase,
        selectedSize:         addonVars.size || 'Medium',  // preserve for display
        icePreference:        isAddonIce,
        isSpicy:              isAddonSpicy,
        customVariantDetails,
        toppingDetails,
        originalItemData:     addonData,
        // ── Store entity variants for sub-row price display ──────────────────
        variants: { size: ev.size, cold: ev.cold, spicy: ev.spicy },
        image_url: addonData.addon_image ? `${baseUrl}${addonData.addon_image}` : addonData.image_url ? `${baseUrl}${addonData.image_url}` : null,
      };

      const cSQ  = buildChildSubQty(childItem);
      const cSub = computeSubTotal({ ...childItem, subItemQuantities: cSQ });
      childItem.subItemQuantities = cSQ;
      childItem.totalPrice        = addonBase * addonQty + cSub;
      childItems.push(childItem);
    });

    // ── Build combo child items ────────────────────────────────────────────────
    Object.entries(updatedItem.comboQuantities || {}).forEach(([comboName, comboQty]) => {
      if (comboQty <= 0) return;
      const comboData = updatedItem.combos?.find(c => (c.name1 || c.name) === comboName);
      if (!comboData) return;

      const comboVars  = updatedItem.comboVariants?.[comboName]       || {};
      const comboCVars = updatedItem.comboCustomVariants?.[comboName] || {};
      const comboTops  = updatedItem.comboToppings?.[comboName]       || {};
      const ev         = getEntityVariants(comboData);

      const rawCombo  = comboData.combo_price || comboData.price || 0;
      const comboBase = ev.size.enabled
        ? sizeAdjustedPrice(ev, comboVars.size, rawCombo)
        : rawCombo;

      const customVariantDetails = [];
      comboData.custom_variants?.forEach(v => {
        if (v.enabled) v.subheadings?.forEach(sub => {
          if (comboCVars[sub.name]) customVariantDetails.push({
            heading: v.heading || v.name || '',
            name: sub.name,
            price: sub.price || 0,
          });
        });
      });

      const toppingDetails = Object.entries(comboTops)
        .filter(([, sel]) => sel)
        .map(([id]) => {
          const t = comboData.toppings?.find(t => t._id === id || t.topping_name === id);
          return t ? { name: t.topping_name || t.name || id, price: t.price || 0 } : null;
        }).filter(Boolean);

      const isComboSpicy = comboVars.spicy || false;
      const isComboIce   = comboVars.cold  ? 'with_ice' : 'without_ice';

      const childItem = {
        _id:                  Math.random().toString(),
        id:                   uuidv4(),
        parentId:             mainItemId,
        item_name:            comboName,
        item_group:           'Combos',
        type:                 'item_combo',
        qty:                  comboQty,
        quantity:             comboQty,
        baseItemPrice:        comboBase,
        standard_selling_rate: comboBase,
        selectedSize:         comboVars.size || 'Medium',
        icePreference:        isComboIce,
        isSpicy:              isComboSpicy,
        customVariantDetails,
        toppingDetails,
        originalItemData:     comboData,
        variants: { size: ev.size, cold: ev.cold, spicy: ev.spicy },
        image_url: comboData.combo_image ? `${baseUrl}${comboData.combo_image}` : comboData.image_url ? `${baseUrl}${comboData.image_url}` : null,
      };

      const cSQ  = buildChildSubQty(childItem);
      const cSub = computeSubTotal({ ...childItem, subItemQuantities: cSQ });
      childItem.subItemQuantities = cSQ;
      childItem.totalPrice        = comboBase * comboQty + cSub;
      childItems.push(childItem);
    });

    // ── Update cart ───────────────────────────────────────────────────────────
    setCart(prev => {
      let newCart = prev.filter(i => i.parentId !== mainItemId);

      if (selectedCartItem && !forceAdd) {
        const idx = newCart.findIndex(i => i.id === selectedCartItem.id);
        if (idx >= 0) newCart[idx] = mainCartEntry;
        else newCart = [...newCart, mainCartEntry];
      } else {
        if (childItems.length === 0 && !forceAdd) {
          const dupIdx = newCart.findIndex(
            i => i.item_name === mainCartEntry.item_name &&
                 i.selectedSize === mainCartEntry.selectedSize &&
                 i.icePreference === mainCartEntry.icePreference &&
                 i.kitchenNotes?.main === mainCartEntry.kitchenNotes?.main &&
                 !i.parentId
          );
          if (dupIdx >= 0) {
            const ex   = newCart[dupIdx];
            const newQ = (ex.qty || 1) + qty;
            const sub  = computeSubTotal(ex);
            newCart[dupIdx] = { ...ex, qty: newQ, quantity: newQ, totalPrice: (ex.baseItemPrice || 0) * newQ + sub };
            setSelectedItem(null); setSelectedCartItem(null);
            return newCart;
          }
        }
        newCart = [...newCart, mainCartEntry];
      }
      return [...newCart, ...childItems];
    });

    setSelectedItem(null); setSelectedCartItem(null);
  };

  // ─── Update parent qty ────────────────────────────────────────────────────────
  const updateQuantity = (itemId, change) => {
    setCart(prev =>
      prev.map(item => {
        if ((item.id || item._id) !== itemId) return item;
        const newQty = (item.qty || item.quantity) + change;
        if (newQty <= 0) return { ...item, qty: 0, quantity: 0 };
        const base = item.baseItemPrice || 0;
        const sub  = computeSubTotal(item);
        return { ...item, qty: newQty, quantity: newQty, totalPrice: base * newQty + sub };
      }).filter(i => (i.qty || i.quantity) > 0)
    );
  };

  // ─── Update sub-option qty (independent per row) ──────────────────────────────
  const updateSubItemQty = (itemId, subKey, change) => {
    setCart(prev =>
      prev.map(item => {
        if ((item.id || item._id) !== itemId) return item;
        const newSubQty      = { ...(item.subItemQuantities || {}) };
        newSubQty[subKey]    = Math.max(1, (newSubQty[subKey] ?? 1) + change);
        const base           = item.baseItemPrice || 0;
        const qty            = item.qty || item.quantity || 1;
        const sub            = computeSubTotal(item, newSubQty);
        return { ...item, subItemQuantities: newSubQty, totalPrice: base * qty + sub };
      })
    );
  };

  // ─── Remove item (and its children) ──────────────────────────────────────────
  const removeItem = (itemId) => {
    setCart(prev => prev.filter(i => (i.id || i._id) !== itemId && i.parentId !== itemId));
  };

  const getCartTotal = () =>
    cart.reduce((sum, i) => sum + (i.totalPrice !== undefined ? i.totalPrice : (i.standard_selling_rate || 0) * (i.quantity || 1)), 0);

  const handleCheckout = () => {
    if (!cart.length) return;
    alert(`Order placed!\nTotal: ₹${getCartTotal().toFixed(2)}\nPlease collect your receipt.`);
    setCart([]); navigate('/home');
  };

  const displayedItems = (
    activeCategory === 'All' ? items : items.filter(i => (i.item_group || 'Uncategorized') === activeCategory)
  ).filter(i => (i.item_name || i.name || '').toLowerCase().includes(searchQuery.toLowerCase()));

  // ─── Group cart ───────────────────────────────────────────────────────────────
  const groupedCart = () => {
    const cmap = {};
    cart.forEach(i => { if (i.parentId) (cmap[i.parentId] = cmap[i.parentId] || []).push(i); });
    return cart.filter(i => !i.parentId).map(parent => ({ parent, children: cmap[parent.id] || [] }));
  };

  // ─── Inline Styles ────────────────────────────────────────────────────────────
  const s = {
    cartPanel:   { backgroundColor: '#fff', display: 'flex', flexDirection: 'column', width: 'var(--k-cart-width)', boxShadow: '-4px 0 20px rgba(0,0,0,0.08)', zIndex: 5 },
    cartHeader:  { padding: '14px 18px', backgroundColor: '#fff', borderBottom: '2px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 },
    cartTitle:   { margin: 0, fontWeight: 800, fontSize: 'var(--k-cart-head-font)', color: '#1e293b' },
    cartBody:    { flex: 1, overflowY: 'auto', backgroundColor: '#fff', padding: '0 14px' },
    cartFooter:  { padding: '14px 18px', backgroundColor: '#fff', borderTop: '2px solid #f1f5f9', flexShrink: 0 },
    totalRow:    { display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontWeight: 800, fontSize: 'var(--k-total-font)', color: '#1e293b' },

    tblHead:     { display: 'grid', gridTemplateColumns: '1fr 88px 64px', gap: '4px', padding: '8px 0 6px', borderBottom: '2px solid #f1f5f9', position: 'sticky', top: 0, background: '#fff', zIndex: 1 },
    thTxt:       { fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' },

    mainGrid:    { display: 'grid', gridTemplateColumns: '1fr 88px 64px', gap: '4px', alignItems: 'center', padding: '10px 0 3px 0' },
    subGrid:     { display: 'grid', gridTemplateColumns: '1fr 88px 64px', gap: '4px', alignItems: 'center', padding: '3px 0 3px 12px', borderLeft: '2px solid #e2e8f0', marginLeft: '4px' },
    childGrid:   { display: 'grid', gridTemplateColumns: '1fr 88px 64px', gap: '4px', alignItems: 'center', padding: '7px 0 3px 6px', marginTop: '2px' },
    csubGrid:    { display: 'grid', gridTemplateColumns: '1fr 88px 64px', gap: '4px', alignItems: 'center', padding: '3px 0 3px 18px', borderLeft: '2px solid #e2e8f0', marginLeft: '10px' },

    qtyWrap:     { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' },
    qBtnMain:    (col) => ({ width: '22px', height: '22px', borderRadius: '50%', background: col, border: col === 'white' ? '1.5px solid #e2e8f0' : 'none', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', color: col === 'white' ? '#475569' : 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', flexShrink: 0 }),
    qBtnSm:      (col) => ({ width: '20px', height: '20px', borderRadius: '50%', background: col, border: col === 'white' ? '1.5px solid #e2e8f0' : 'none', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', color: col === 'white' ? '#475569' : 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', flexShrink: 0 }),
    qBtnXs:      (col) => ({ width: '18px', height: '18px', borderRadius: '50%', background: col, border: col === 'white' ? '1.5px solid #e2e8f0' : 'none', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', color: col === 'white' ? '#475569' : 'white', flexShrink: 0 }),
    qNum:        (sz)  => ({ fontSize: sz || '13px', fontWeight: 800, color: '#1e293b', minWidth: '14px', textAlign: 'center' }),

    nameMain:    { fontWeight: 800, fontSize: 'clamp(0.8rem,1.8vw,1rem)', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer', flex: 1 },
    nameSub:     { fontWeight: 600, fontSize: '12px', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 },
    nameChild:   { fontWeight: 700, fontSize: '13px', color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 },
    nameCSub:    { fontWeight: 600, fontSize: '11px', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 },

    priceMain:   { textAlign: 'right', fontWeight: 800, fontSize: '14px', color: '#1e293b', whiteSpace: 'nowrap' },
    priceSub:    { textAlign: 'right', fontWeight: 600, fontSize: '12px', color: '#94a3b8', whiteSpace: 'nowrap' },
    priceChild:  { textAlign: 'right', fontWeight: 700, fontSize: '13px', color: '#475569', whiteSpace: 'nowrap' },
    priceCSub:   { textAlign: 'right', fontWeight: 600, fontSize: '11px', color: '#cbd5e1', whiteSpace: 'nowrap' },

    trashMain:   { width: '22px', height: '22px', borderRadius: '5px', border: '1.5px solid #fca5a5', background: '#fff5f5', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0, flexShrink: 0 },
    trashSm:     { width: '18px', height: '18px', borderRadius: '4px', border: '1.5px solid #fca5a5', background: '#fff5f5', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0, flexShrink: 0 },

    nutriBtn:    { background: 'linear-gradient(135deg,#d1fae5,#a7f3d0)', color: '#059669', border: 'none', borderRadius: '5px', padding: '2px 5px', fontSize: '10px', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '3px', whiteSpace: 'nowrap', flexShrink: 0 },
    nutriBtnSm:  { background: 'linear-gradient(135deg,#e0f2fe,#bae6fd)', color: '#0369a1', border: 'none', borderRadius: '4px', padding: '1px 4px', fontSize: '9px', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '2px', whiteSpace: 'nowrap', flexShrink: 0 },

    addonBadge:  { background: '#eff6ff', color: '#3b82f6', borderRadius: '4px', padding: '1px 5px', fontSize: '10px', fontWeight: 700, flexShrink: 0, whiteSpace: 'nowrap' },
    comboBadge:  { background: '#fdf4ff', color: '#9333ea', borderRadius: '4px', padding: '1px 5px', fontSize: '10px', fontWeight: 700, flexShrink: 0, whiteSpace: 'nowrap' },

    groupWrap:   { borderBottom: '1px solid #f1f5f9', paddingBottom: '6px', marginBottom: '2px' },
    nameRow:     { display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0, overflow: 'hidden' },
  };

  // ─── Qty controls ─────────────────────────────────────────────────────────────
  const QtyMain  = ({ item }) => (
    <div style={s.qtyWrap}>
      <button style={s.qBtnMain('white')}   onClick={() => updateQuantity(item.id || item._id, -1)}><FaMinus style={{ fontSize: '8px' }} /></button>
      <span   style={s.qNum('13px')}>{item.qty || item.quantity}</span>
      <button style={s.qBtnMain('#e4002b')} onClick={() => updateQuantity(item.id || item._id,  1)}><FaPlus  style={{ fontSize: '8px' }} /></button>
    </div>
  );
  const QtySub   = ({ itemId, subKey, qty }) => (
    <div style={s.qtyWrap}>
      <button style={s.qBtnSm('white')}   onClick={() => updateSubItemQty(itemId, subKey, -1)}><FaMinus style={{ fontSize: '7px' }} /></button>
      <span   style={s.qNum('12px')}>{qty}</span>
      <button style={s.qBtnSm('#f97316')} onClick={() => updateSubItemQty(itemId, subKey,  1)}><FaPlus  style={{ fontSize: '7px' }} /></button>
    </div>
  );
  const QtyChild = ({ item }) => (
    <div style={s.qtyWrap}>
      <button style={s.qBtnSm('white')}   onClick={() => updateQuantity(item.id || item._id, -1)}><FaMinus style={{ fontSize: '7px' }} /></button>
      <span   style={s.qNum('12px')}>{item.qty || item.quantity}</span>
      <button style={s.qBtnSm('#6366f1')} onClick={() => updateQuantity(item.id || item._id,  1)}><FaPlus  style={{ fontSize: '7px' }} /></button>
    </div>
  );
  const QtyCSub  = ({ itemId, subKey, qty }) => (
    <div style={s.qtyWrap}>
      <button style={s.qBtnXs('white')}   onClick={() => updateSubItemQty(itemId, subKey, -1)}><FaMinus style={{ fontSize: '6px' }} /></button>
      <span   style={s.qNum('11px')}>{qty}</span>
      <button style={s.qBtnXs('#6366f1')} onClick={() => updateSubItemQty(itemId, subKey,  1)}><FaPlus  style={{ fontSize: '6px' }} /></button>
    </div>
  );

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="kiosk-page-root">

      {/* Simulator Toolbar */}
      <div className="simulator-toolbar d-flex align-items-center justify-content-between flex-wrap py-2 px-4 bg-dark text-white w-100">
        <div style={{ width: '200px' }} className="d-none d-lg-block" />
        <div className="d-flex align-items-center justify-content-center flex-wrap">
          <span className="me-3 fw-bold">Select Machine Size:</span>
          {simulatorModes.map(m => (
            <button key={m.name} className={`btn btn-sm mx-1 my-1 ${activeSimulator === m.name ? 'btn-danger' : 'btn-outline-light'}`} onClick={() => setActiveSimulator(m.name)}>
              {m.label}
            </button>
          ))}
        </div>
        <div className="d-flex align-items-center justify-content-end" style={{ minWidth: '200px' }}>
          <span className="me-2 fw-bold text-white-50" style={{ fontSize: '14px' }}>Orientation:</span>
          <button className={`btn btn-sm mx-1 my-1 fw-bold ${orientation === 'landscape' ? 'btn-info text-dark' : 'btn-outline-info'}`} onClick={() => setOrientation('landscape')}>▭ Horizontal</button>
          <button className={`btn btn-sm mx-1 my-1 fw-bold ${orientation === 'portrait'  ? 'btn-info text-dark' : 'btn-outline-info'}`} onClick={() => setOrientation('portrait')}>▯ Vertical</button>
        </div>
      </div>

      {/* Kiosk Container */}
      <div className={`simulator-wrapper ${activeSimulator !== 'Native' ? 'sim-active' : ''}`} data-size={activeSimulator}>
        <div className={`kiosk-container ${orientation === 'portrait' ? 'portrait-mode' : ''}`} style={{ position: 'relative' }}>

        {/* Edit Variant Modal */}
        {selectedItem && (
          <KioskVariantModal item={selectedItem} cartItem={selectedCartItem}
            onClose={() => { setSelectedItem(null); setSelectedCartItem(null); }}
            onUpdate={handleItemUpdate} />
        )}

        {/* Nutrition-only Modal */}
        {nutritionItem && (
          <KioskVariantModal item={nutritionItem} cartItem={null}
            onClose={() => setNutritionItem(null)}
            onUpdate={() => setNutritionItem(null)}
            autoOpenNutrition={true} />
        )}

          {/* Header */}
          <header className="kiosk-header" style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: '10px' }}>
            <div className="d-flex align-items-center" onClick={() => navigate('/home')} style={{ cursor: 'pointer', overflow: 'hidden' }}>
              <div style={{ backgroundColor: 'white', color: 'var(--kiosk-primary)', padding: '5px 12px', borderRadius: '50px', fontWeight: 'bold', marginRight: '15px', flexShrink: 0 }}>
                ← Exit
              </div>
              <h1 className="mb-0 d-none d-lg-block text-truncate" style={{ fontSize: '1.6rem', whiteSpace: 'nowrap' }}>Self Ordering Kiosk</h1>
            </div>
            <div className="d-flex justify-content-center" style={{ width: '450px', maxWidth: '100%' }}>
              <div className="position-relative w-100">
                <FaSearch className="position-absolute text-muted" style={{ top: '50%', left: '15px', transform: 'translateY(-50%)' }} />
                <input type="text" className="form-control" placeholder="Search for your favourite items..."
                  value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  style={{ borderRadius: '50px', padding: '12px 20px 12px 45px', fontSize: '1.1rem', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }} />
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <button className="btn btn-light rounded-pill px-3 px-md-4 fw-bold text-danger" onClick={() => setCart([])} style={{ whiteSpace: 'nowrap' }}>Start Over</button>
            </div>
          </header>

          <main className="kiosk-main">

            {/* Categories */}
            <aside className="kiosk-sidebar" style={orientation === 'portrait' ? { alignItems: 'center' } : {}}>
              <h4 className="fw-bold text-muted" style={orientation === 'portrait' ? { marginBottom: 0, marginRight: '20px', flexShrink: 0 } : { marginBottom: '1rem' }}>
                Categories
              </h4>
              <div style={orientation === 'portrait' ? { marginRight: '20px', flexShrink: 0, width: '220px' } : { marginBottom: '1rem' }}>
                <div className="position-relative">
                  <FaSearch className="position-absolute text-muted" style={{ top: '50%', left: '10px', transform: 'translateY(-50%)' }} />
                  <input type="text" className="form-control" placeholder="Search categories..."
                    value={categorySearchQuery} onChange={e => setCategorySearchQuery(e.target.value)}
                    style={{ borderRadius: '10px', paddingLeft: '35px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', height: '45px' }} />
                </div>
              </div>
              <div className="kiosk-category-list" style={{ display: 'flex', flexDirection: orientation === 'portrait' ? 'row' : 'column', flexWrap: 'nowrap' }}>
                {categories.filter(c => c.toLowerCase().includes(categorySearchQuery.toLowerCase())).map(cat => (
                  <button key={cat} className={`kiosk-category-btn ${activeCategory === cat ? 'active' : ''}`}
                    onClick={() => setActiveCategory(cat)}
                    style={orientation === 'portrait' ? { flexShrink: 0, margin: '0 15px 0 0', width: 'auto', minWidth: '120px', padding: '10px 25px', whiteSpace: 'nowrap' } : {}}>
                    {cat}
                  </button>
                ))}
              </div>
            </aside>

            {/* Items Grid */}
            <section className="kiosk-content">
              <div className="container-fluid p-0">
                <div className="row g-4">
                  {loading ? (
                    <div className="col-12 text-center mt-5">
                      <div className="spinner-border text-danger" style={{ width: '3rem', height: '3rem' }} role="status" />
                    </div>
                  ) : displayedItems.length === 0 ? (
                    <div className="col-12 text-center mt-5">
                      <h4 className="text-muted">No items found{searchQuery ? ` matching "${searchQuery}"` : ''}</h4>
                    </div>
                  ) : displayedItems.map(item => (
                    <div className="col-12 col-md-6 col-lg-4 col-xl-3" key={item._id}>
                      <div className="card kiosk-card">
                        <div className="kiosk-card-img-wrapper">
                          {item.image_url
                            ? <img src={item.image_url} alt="" className="kiosk-card-img" />
                            : <div className="kiosk-card-img d-flex align-items-center justify-content-center bg-light text-muted h1 m-0">🍔</div>}
                        </div>
                        <div className="card-body kiosk-card-body">
                          <h5 className="kiosk-card-title">{item.item_name}</h5>
                          <div className="kiosk-price">₹{item.standard_selling_rate?.toFixed(2) || '0.00'}</div>
                          <button className="kiosk-add-btn" onClick={() => addToCart(item)}>Add to Order</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* ── Cart Panel ── */}
            <aside style={s.cartPanel}>

              <div style={s.cartHeader}>
                <h3 style={s.cartTitle}>Your Order</h3>
                {cart.length > 0 && (
                  <button onClick={() => setCart([])}
                    style={{ background: 'none', border: '1px solid #fca5a5', color: '#dc2626', borderRadius: '6px', padding: '3px 8px', fontSize: '12px', cursor: 'pointer', fontWeight: 600 }}>
                    Clear
                  </button>
                )}
              </div>

              <div style={s.cartBody}>
                {cart.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <div style={{ fontSize: '48px', marginBottom: '12px' }}>🛒</div>
                    <div style={{ fontWeight: 700, color: '#64748b', fontSize: '16px' }}>Your cart is empty</div>
                    <div style={{ color: '#94a3b8', fontSize: '13px', marginTop: '4px' }}>Add some delicious items!</div>
                  </div>
                ) : (
                  <>
                    <div style={s.tblHead}>
                      <span style={s.thTxt}>Items</span>
                      <span style={{ ...s.thTxt, textAlign: 'center' }}>Qty</span>
                      <span style={{ ...s.thTxt, textAlign: 'right'  }}>Price</span>
                    </div>

                    {groupedCart().map(({ parent, children }) => {
                      const parentSubs = getSubRows(parent);
                      return (
                        <div key={parent.id || parent._id} style={s.groupWrap}>

                          {/* ═══ MAIN ITEM ═══ */}
                          <div style={s.mainGrid}>
                            <div style={s.nameRow}>
                              <div style={s.nameMain}
                                onClick={() => { setSelectedItem(parent); setSelectedCartItem(parent); }}
                                title={`${parent.item_name}${parent.selectedSize ? ` (${parent.selectedSize})` : ''}`}>
                                {parent.item_name}{parent.selectedSize ? ` (${parent.selectedSize})` : ''}
                              </div>
                              <button style={s.nutriBtn} onClick={() => setNutritionItem(parent)}>
                                <FaLeaf style={{ fontSize: '9px' }} /> Nutrition
                              </button>
                              <button style={s.trashMain} onClick={e => { e.stopPropagation(); removeItem(parent.id || parent._id); }}>
                                <FaTrash style={{ fontSize: '8px' }} />
                              </button>
                            </div>
                            <QtyMain item={parent} />
                            {/* Size-adjusted base price */}
                            <div style={s.priceMain}>₹{(parent.baseItemPrice || 0).toFixed(2)}</div>
                          </div>

                          {/* ── Main sub-rows ── */}
                          {parentSubs.map(row => (
                            <div key={row.key} style={s.subGrid}>
                              <div style={s.nameSub} title={`+ ${row.label}`}>+ {row.label}</div>
                              <QtySub itemId={parent.id || parent._id} subKey={row.key} qty={row.qty} />
                              <div style={s.priceSub}>₹{row.unitPrice.toFixed(2)}</div>
                            </div>
                          ))}

                          {/* ── Child Items ── */}
                          {children.map(child => {
                            const childSubs = getSubRows(child);
                            return (
                              <div key={child.id || child._id} style={{ marginTop: '4px', paddingLeft: '6px', borderLeft: '3px solid #f1f5f9' }}>

                                {/* ═══ CHILD ROW ═══ */}
                                <div style={s.childGrid}>
                                  <div style={s.nameRow}>
                                    <span style={child.type === 'addon' ? s.addonBadge : s.comboBadge}>
                                      {child.type === 'addon' ? 'Addon' : 'Combo'}
                                    </span>
                                    <div style={s.nameChild} title={`${child.item_name}${child.selectedSize ? ` (${child.selectedSize})` : ''}`}>
                                      {child.item_name}{child.selectedSize ? ` (${child.selectedSize})` : ''}
                                    </div>
                                    <button style={s.nutriBtnSm} onClick={() => setNutritionItem(child.originalItemData || child)}>
                                      <FaLeaf style={{ fontSize: '8px' }} />
                                    </button>
                                    <button style={s.trashSm} onClick={e => { e.stopPropagation(); removeItem(child.id || child._id); }}>
                                      <FaTrash style={{ fontSize: '7px' }} />
                                    </button>
                                  </div>
                                  <QtyChild item={child} />
                                  {/* Size-adjusted base price — now correctly ₹5 for Medium Extra Chicken */}
                                  <div style={s.priceChild}>₹{(child.baseItemPrice || 0).toFixed(2)}</div>
                                </div>

                                {/* ── Child sub-rows ── */}
                                {childSubs.map(row => (
                                  <div key={row.key} style={s.csubGrid}>
                                    <div style={s.nameCSub} title={`+ ${row.label}`}>+ {row.label}</div>
                                    <QtyCSub itemId={child.id || child._id} subKey={row.key} qty={row.qty} />
                                    <div style={s.priceCSub}>₹{row.unitPrice.toFixed(2)}</div>
                                  </div>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>

              <div style={s.cartFooter}>
                <div style={s.totalRow}>
                  <span>Total</span>
                  <span style={{ color: '#e4002b' }}>₹{getCartTotal().toFixed(2)}</span>
                </div>
                <button className="kiosk-checkout-btn" disabled={cart.length === 0} onClick={handleCheckout}>
                  Checkout &amp; Pay
                </button>
              </div>
            </aside>

          </main>
        </div>
      </div>
    </div>
  );
};

export default KioskPage;
